import React, { useLayoutEffect, useRef, useState } from 'react'

import * as S from './page.styled'

// Note: dispatched (bubbling) by a flex-growing descendant whose own explicit min-height just
// changed - see TableGroup. Page reads such a child's min-height, not its rendered height (see
// `measure` below), but a *shrinking* min-height doesn't resize anything Page observes: the child
// still stretches to fill Page, which is itself still held open by its own stale min-height. So
// no ResizeObserver notification ever fires, and without this event Page would stay stuck at the
// taller value (e.g. a leftover page scroll after collapsing a table's filters row).
export const PAGE_REMEASURE_EVENT = 'page:remeasure'

export type PageProps = {
  // Note: caps the page at the xxl breakpoint (1920px) and centers it on wider screens. Added only for the
  // Place details and Client profile pages - every other page still stretches to full width.
  centered?: boolean
  children: React.ReactNode
  // Note: set to false for content that's meant to overflow into page-level scroll (e.g. a
  // plain form/details view) rather than stretch-and-scroll-internally (e.g. a table) - see
  // the note on `page.styled.ts`'s `PageWrapper`'s `min-height`.
  fillHeight?: boolean
}

export const Page = ({ centered = false, children, fillHeight = true }: PageProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Note: this Page's true minimum height, measured (not guessed) from its own direct
  // children's actual rendered sizes - see PageWrapper's own comment for why this has to be a
  // real measurement rather than a CSS min-content/auto value. 0 until the first observation
  // fires, which is fine since `flex: 1` still stretches this to fill available space in the
  // meantime, same as it always did.
  const [minHeight, setMinHeight] = useState(0)
  const childCount = React.Children.count(children)

  useLayoutEffect(() => {
    const wrapperElement = wrapperRef.current
    if (!wrapperElement || !fillHeight) return

    const measure = () => {
      const childElements = Array.from(wrapperElement.children) as HTMLElement[]
      const wrapperStyle = getComputedStyle(wrapperElement)
      // Note: read padding/gap from computed style rather than duplicating page.styled.ts's
      // breakpoint-varying values here - stays correct as those change without this file
      // needing to track them too.
      const paddingTop = Number.parseFloat(wrapperStyle.paddingTop) || 0
      const paddingBottom = Number.parseFloat(wrapperStyle.paddingBottom) || 0
      const gap = Number.parseFloat(wrapperStyle.rowGap || wrapperStyle.gap) || 0

      // Note: a flex-growing child (e.g. TableGroup's Card, `flex: 1`) stretches to fill
      // whatever height Page gives it - reading its *rendered* height back into Page's own
      // min-height is circular, and can permanently lock Page onto a stale, too-tall value: if
      // a resize/hidden-tab sequence (e.g. toggling DevTools) catches that child at a transient
      // size (its own Header briefly reflowing at an in-between width), Page bakes that in
      // forever, since nothing further nudges Page to re-measure once its own children settle
      // back down. Such a child's own CSS `min-height` (its true, stable floor - see Card's own
      // styled-component comment) sidesteps the circularity entirely; only fall back to its
      // rendered height when it has no explicit min-height to read, preserving prior behavior
      // for every other kind of child.
      const childrenHeight = childElements.reduce((sum, element) => {
        const childStyle = getComputedStyle(element)
        const isFlexGrowing = Number.parseFloat(childStyle.flexGrow) > 0
        const explicitMinHeight = Number.parseFloat(childStyle.minHeight)
        const height =
          isFlexGrowing && explicitMinHeight > 0
            ? explicitMinHeight
            : element.getBoundingClientRect().height
        return sum + height
      }, 0)
      const gapsHeight = gap * Math.max(0, childElements.length - 1)

      setMinHeight(paddingTop + paddingBottom + childrenHeight + gapsHeight)
    }

    // Note: the *initial* measurement stays synchronous (not scheduled - see scheduleMeasure
    // below) deliberately - this reads each direct child's rendered height, and one of those
    // children may be a TableGroup Card with its own equivalent layout effect. React fires
    // layout effects bottom-up, resolving a child's state update (and the synchronous re-render
    // it triggers) *before* this parent's own layout effect runs - deferring this first call to
    // a later frame would read that child's still-uncorrected height instead.
    measure()

    // Note: a rapid, cascading resize (e.g. DevTools' device-toolbar toggle, which resizes many
    // elements across breakpoints in one go) can hit the browser's ResizeObserver loop-limit,
    // silently dropping a notification for one of these elements - since nothing else nudges
    // that element's size afterward, there's no further trigger to correct the stale measurement
    // it left behind, even switching device mode back off. `requestAnimationFrame` defers the
    // *re-measurement* to after the browser has finished the resize's layout pass (rather than
    // mid-cascade).
    let animationFrame: number | null = null
    const scheduleMeasure = () => {
      if (animationFrame !== null) return
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null
        measure()
      })
    }

    // Note: two independent fallback triggers, deliberately calling `measure()` directly rather
    // than `scheduleMeasure` - `requestAnimationFrame` (and, by extension, ResizeObserver's own
    // notification delivery) is suspended entirely while the tab is hidden/backgrounded, which a
    // DevTools device-toolbar toggle can transiently cause. `resize` still fires reliably in that
    // state, and `visibilitychange` firing when the tab becomes visible again is what guarantees
    // this self-corrects once whatever suspended rendering ends - neither depends on rAF/the
    // observer having been able to deliver anything during the hidden window.
    const handleWindowResize = () => measure()
    const handleVisibilityChange = () => {
      if (!document.hidden) measure()
    }

    const observer = new ResizeObserver(scheduleMeasure)
    observer.observe(wrapperElement)
    for (const child of wrapperElement.children) {
      observer.observe(child)
    }

    // Note: measured synchronously, not via scheduleMeasure - the dispatching child fires this from
    // its own layout effect, after its new min-height is already committed to the DOM, so Page
    // corrects in the same frame instead of flashing the stale scrollbar for one.
    const handleRemeasure = () => measure()

    window.addEventListener('resize', handleWindowResize)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    wrapperElement.addEventListener(PAGE_REMEASURE_EVENT, handleRemeasure)

    return () => {
      observer.disconnect()
      wrapperElement.removeEventListener(PAGE_REMEASURE_EVENT, handleRemeasure)
      window.removeEventListener('resize', handleWindowResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (animationFrame !== null) cancelAnimationFrame(animationFrame)
    }
    // Note: re-run (re-attaching to the current children) when the number of direct children
    // changes - a child's own size *changing* is already handled by the ResizeObserver callback
    // itself, independent of React's render cycle.
  }, [fillHeight, childCount])

  return (
    <S.PageWrapper
      ref={wrapperRef}
      $centered={centered}
      $fillHeight={fillHeight}
      $minHeightPx={minHeight}
      data-centered={centered || undefined}
      data-component-id="Page"
    >
      {children}
    </S.PageWrapper>
  )
}
